<template>
  <div class="page-view">
    <div class="header-row">
      <div>
        <h2>业务类型</h2>
        <p>维护业务类型，并按业务类型勾选可用管理员。</p>
      </div>
      <el-button type="primary" @click="openCreate">新增业务类型</el-button>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model="filters.keyword" placeholder="业务类型名称" clearable />
        <el-select v-model="filters.is_active" placeholder="状态" clearable>
          <el-option label="启用" :value="true" />
          <el-option label="停用" :value="false" />
        </el-select>
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table :data="items" stripe>
        <el-table-column prop="id" label="业务类型ID" width="120" />
        <el-table-column prop="name" label="业务类型" min-width="160" />
        <el-table-column prop="created_by_name" label="创建人" min-width="120" />
        <el-table-column v-if="currentUser.role === 'super_admin'" label="可用管理员" min-width="520">
          <template slot-scope="{ row }">
            <el-checkbox-group v-if="canAssignAdmins(row)" v-model="row.admin_ids">
              <el-checkbox v-for="leader in leaders" :key="leader.id" :label="leader.id">
                {{ leader.name }}
              </el-checkbox>
            </el-checkbox-group>
            <span v-else>{{ (row.admin_names || []).join("、") || "-" }}</span>
          </template>
        </el-table-column>
        <el-table-column v-else label="可用管理员" min-width="220">
          <template slot-scope="{ row }">{{ (row.admin_names || []).join("、") || "-" }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">{{ row.is_active ? "启用" : "停用" }}</template>
        </el-table-column>
        <el-table-column :width="currentUser.role === 'super_admin' ? 360 : 260" label="操作" fixed="right">
          <template slot-scope="{ row }">
            <el-button v-if="canAssignAdmins(row)" size="mini" type="primary" plain @click="saveAdmins(row)">
              保存管理员
            </el-button>
            <template v-if="canManageItem(row)">
              <el-button size="mini" @click="openEdit(row)">编辑名称</el-button>
              <el-button size="mini" plain @click="toggleStatus(row)">{{ row.is_active ? "停用" : "启用" }}</el-button>
              <el-button size="mini" type="danger" plain @click="removeItem(row)">删除</el-button>
            </template>
            <span v-else class="readonly-text">仅可使用</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog :title="editingId ? '编辑业务类型' : '新增业务类型'" :visible.sync="showDialog" width="520px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="业务类型名称">
          <el-input v-model="form.name" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" @click="submitForm">提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { createBusinessType, deleteBusinessType, fetchBusinessTypes, fetchLeaders, updateBusinessType } from "../api/orders";
import { getStoredUser } from "../utils/auth";

const emptyForm = () => ({ name: "" });

export default {
  data() {
    return {
      currentUser: getStoredUser(),
      filters: { keyword: "", is_active: "" },
      items: [],
      leaders: [],
      total: 0,
      pager: { page: 1, page_size: 10 },
      showDialog: false,
      editingId: null,
      form: emptyForm(),
    };
  },
  mounted() {
    this.loadItems();
    this.loadLeaders();
  },
  methods: {
    async loadItems() {
      const { data } = await fetchBusinessTypes({
        keyword: this.filters.keyword || undefined,
        is_active: this.filters.is_active === "" ? undefined : this.filters.is_active,
        page: this.pager.page,
        page_size: this.pager.page_size,
      });
      this.items = (data.items || []).map((item) => ({
        ...item,
        admin_ids: [...(item.admin_ids || [])],
      }));
      this.total = data.total || 0;
    },
    async loadLeaders() {
      const { data } = await fetchLeaders();
      this.leaders = data || [];
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadItems();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadItems();
    },
    onSizeChange(size) {
      this.pager.page = 1;
      this.pager.page_size = size;
      this.loadItems();
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.showDialog = true;
    },
    openEdit(row) {
      this.editingId = row.id;
      this.form = { name: row.name };
      this.showDialog = true;
    },
    canManageItem(row) {
      return this.currentUser.role === "super_admin" || row.created_by_id === this.currentUser.id;
    },
    canAssignAdmins(row) {
      return this.currentUser.role === "super_admin" && row.created_by_role === "super_admin";
    },
    async submitForm() {
      if (this.editingId) {
        await updateBusinessType(this.editingId, { name: this.form.name });
        this.$message.success("更新成功");
      } else {
        const payload = { name: this.form.name };
        if (this.currentUser.role === "admin") {
          payload.admin_ids = [this.currentUser.id];
        }
        await createBusinessType(payload);
        this.$message.success("创建成功");
      }
      this.showDialog = false;
      this.loadItems();
    },
    async saveAdmins(row) {
      await updateBusinessType(row.id, { admin_ids: row.admin_ids });
      this.$message.success(`已保存 ${row.name} 的管理员配置`);
      this.loadItems();
    },
    async toggleStatus(row) {
      await updateBusinessType(row.id, { is_active: !row.is_active });
      this.$message.success("状态已更新");
      this.loadItems();
    },
    async removeItem(row) {
      await this.$confirm(`确认删除业务类型 ${row.name} 吗？`, "删除业务类型", { type: "warning" });
      await deleteBusinessType(row.id);
      this.$message.success("删除成功");
      this.loadItems();
    },
  },
};
</script>

<style scoped>

:deep(.el-checkbox-group) {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

:deep(.el-checkbox) {
  margin-right: 0;
}

.readonly-text {
  color: #8b97aa;
  font-size: 13px;
}
</style>
